import { Link, createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { publicTargetQuery, publicTargetStatsQuery, publicTargetTimelineQuery } from '@/api/queries'
import type { StatsWindow } from '@/api/types'
import { DEFAULT_WINDOW, validateWindowSearch } from '@/lib/windowSearch'
import { ResponseTimeChart, StatCard, UptimeBars, WindowPicker } from '@/components/Charts'
import { StatusBadge } from '@/components/StatusBadge'
import { UptimeDays } from '@/components/UptimeDays'
import { Card, Loading } from '@/components/ui'
import { formatMs, formatPercent, formatRelative } from '@/lib/format'

/**
 * Verejny detail jednoho cile: /public/target/{id}.
 *
 * Ukazuje jen to, co se da sdilet ven — stav, SLA a odezvu. URL cile,
 * ocekavany kod ani text chyby tu zamerne nejsou (viz PublicTargetResponse
 * na backendu).
 */
export const Route = createFileRoute('/public/target/$targetId')({
  validateSearch: validateWindowSearch,
  loader: ({ context, params }) => context.queryClient.ensureQueryData(publicTargetQuery(params.targetId)),
  component: PublicTargetPage,
  errorComponent: () => (
    <Shell>
      <Card className="text-center text-sm text-slate-500">Tenhle cil neexistuje nebo neni verejny.</Card>
    </Shell>
  ),
})

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-3xl space-y-6">{children}</div>
    </div>
  )
}

function PublicTargetPage() {
  const { targetId } = Route.useParams()
  const { window = DEFAULT_WINDOW } = Route.useSearch()
  const navigate = Route.useNavigate()
  const setWindow = (next: StatsWindow) => navigate({ search: { window: next }, replace: true })

  const { data, isLoading } = useQuery(publicTargetQuery(targetId))
  const { data: stats } = useQuery(publicTargetStatsQuery(targetId, window))
  const { data: timeline } = useQuery(publicTargetTimelineQuery(targetId, window))

  if (isLoading || !data) {
    return (
      <Shell>
        <Loading />
      </Shell>
    )
  }

  return (
    <Shell>
      <div className="text-center">
        {data.servicePublicId && (
          <Link
            to="/status/$publicId"
            params={{ publicId: data.servicePublicId }}
            className="text-sm text-slate-500 hover:text-brand-700"
          >
            ← {data.serviceName}
          </Link>
        )}
        <h1 className="mt-1 text-2xl font-bold text-slate-900">{data.target.name}</h1>
        <div className="mt-3 flex justify-center">
          <StatusBadge status={data.target.status} />
        </div>
        <p className="mt-2 text-xs text-slate-400">
          Posledni kontrola {formatRelative(data.target.lastCheckedAt)}
        </p>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-slate-700">Historie</h2>
        <WindowPicker value={window} onChange={setWindow} />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatCard
          label={`SLA ${window}`}
          value={formatPercent(stats?.uptimePercent)}
          tone={stats?.uptimePercent === 100 ? 'good' : (stats?.uptimePercent ?? 100) < 99 ? 'bad' : 'warn'}
        />
        <StatCard label="Prumerna odezva" value={formatMs(stats?.avgResponseMs)} />
        <StatCard label="p95 odezva" value={formatMs(stats?.p95ResponseMs)} />
      </div>

      {/* Denni pruh za 90 dnu — stejny pohled jako na status strance sluzby,
          jen pro jeden cil. Nezavisi na zvolenem okne nahore. */}
      <Card>
        <div className="mb-3 flex items-baseline justify-between">
          <h3 className="text-sm font-medium text-slate-700">Poslednich 90 dnu</h3>
          <span className="text-xs text-slate-400">
            {data.target.uptime90d === null ? 'bez dat' : `${formatPercent(data.target.uptime90d)} dostupnost`}
          </span>
        </div>
        <UptimeDays days={data.target.daily} />
      </Card>

      <Card>
        <h3 className="mb-3 text-sm font-medium text-slate-700">Dostupnost ve zvolenem obdobi</h3>
        <UptimeBars data={timeline ?? []} />
      </Card>

      <Card>
        <h3 className="mb-3 text-sm font-medium text-slate-700">Doba odezvy</h3>
        <ResponseTimeChart data={timeline ?? []} window={window} />
      </Card>

      <p className="text-center text-xs text-slate-400">monitoring.individualstartup.cz</p>
    </Shell>
  )
}
